import React, { useEffect } from "react";
import { Batch, ProductType, SerialRange } from "generated/graphql";
import { format } from "date-fns";

import { PageTemplate } from "@components/pageTemplate/PageTemplate";
import { DependencyType } from "@di/DependencyType";
import { useInjection } from "@di/DependencyContext";
import { BatchService } from "@modules/batch/batch.service";
import { ProductTypeService } from "@modules/product-type/ProductType.service";
import { SerialRangeService } from "@modules/serial-range/serial-range.service";
import { LoadingState } from "@util/loadingstate.util";
import { BatchForm } from "./forms/BatchForm";
import { SerialRangeForm } from "./forms/SerialRangeForm";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Button } from "~/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Dialog, DialogContent } from "~/components/ui/dialog";
import { Spinner } from "~/components/ui/spinner";

const SERIAL_RANGE_TABLE_COLUMN_COUNT = 10;

const formatSerialNumber = (serialNumber: number) =>
  serialNumber.toString().padStart(7, "0");

const getRemainingSequentialSerials = (serialRange: SerialRange) => {
  if (serialRange.nextAvailable === -1) {
    return 0;
  }

  const remaining = serialRange.endNumber - serialRange.nextAvailable + 1;
  return remaining < 0 ? 0 : remaining;
};

export const ProductSetupPage = () => {
  const [batches, setBatches] = React.useState<Batch[]>([]);
  const [serialRanges, setSerialRanges] = React.useState<SerialRange[]>([]);
  const [productTypes, setProductTypes] = React.useState<ProductType[]>([]);
  const [batchLoadingState, setBatchLoadingState] =
    React.useState<LoadingState>(LoadingState.Loading);
  const [serialRangeLoadingState, setSerialRangeLoadingState] =
    React.useState<LoadingState>(LoadingState.Loading);
  const [openModal, setOpenModal] = React.useState<"batch" | "serial">();
  const [selectedBatch, setSelectedBatch] = React.useState<Batch>();
  const [selectedSerialRange, setSelectedSerialRange] =
    React.useState<SerialRange>();
  const serialRangeService = useInjection<SerialRangeService>(
    DependencyType.SerialRangeService
  );
  const batchService = useInjection<BatchService>(DependencyType.BatchService);
  const productTypeService = useInjection<ProductTypeService>(
    DependencyType.ProductTypeService
  );

  const updateBatches = async (refresh = false) => {
    try {
      setBatchLoadingState(LoadingState.Loading);
      const batches = await batchService.getAllBatches(refresh);
      setBatches(batches as Batch[]);
      setBatchLoadingState(LoadingState.Loaded);
    } catch (e) {
      console.error(e);
      setBatchLoadingState(LoadingState.Error);
    }
  };

  const updateSerialRanges = async (refresh = false) => {
    try {
      setSerialRangeLoadingState(LoadingState.Loading);
      const serialRanges = await serialRangeService.getAllSerialRanges(refresh);
      setSerialRanges(serialRanges as SerialRange[]);
      setSerialRangeLoadingState(LoadingState.Loaded);
    } catch (e) {
      console.error("Unable to load serial ranges", e);
      setSerialRangeLoadingState(LoadingState.Error);
    }
  };

  const updateProductTypes = async (refresh = false) => {
    try {
      const productTypes = await productTypeService.getAllProductTypes(refresh);
      setProductTypes(productTypes as ProductType[]);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    (() => {
      void updateBatches();
      void updateSerialRanges();
      void updateProductTypes();
    })();
  }, []);

  const openCreateBatchModal = () => {
    setSelectedBatch(undefined);
    setOpenModal("batch");
  };

  const openEditBatchModal = (batch: Batch) => {
    setSelectedBatch(batch);
    setOpenModal("batch");
  };

  const openCreateSerialRangeModal = () => {
    setSelectedSerialRange(undefined);
    setOpenModal("serial");
  };

  const openEditSerialRangeModal = (serialRange: SerialRange) => {
    setSelectedSerialRange(serialRange);
    setOpenModal("serial");
  };

  return (
    <PageTemplate
      activeItem="Admin"
      activeSubItem="Product Setup"
      title="Product setup"
      subtitle="Product codes, batch and serial numbers are used to track the manufacturing and movement of PES products through the supply chain."
    >
      <div className="flex flex-col gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Batch numbers</CardTitle>
            <CardAction>
              <Button onClick={openCreateBatchModal}>Add batch</Button>
            </CardAction>
          </CardHeader>
          <CardContent>
            {batchLoadingState === LoadingState.Loading ? (
              <div className="flex h-24 items-center justify-center">
                <Spinner className="size-5" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Batch number</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batchLoadingState === LoadingState.Error ? (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className="text-center text-sm text-destructive"
                      >
                        Unable to load batches.
                      </TableCell>
                    </TableRow>
                  ) : batches.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className="text-center text-sm text-muted-foreground"
                      >
                        No batches yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    batches.map((batch) => (
                      <TableRow key={batch.id}>
                        <TableCell className="font-medium">
                          {batch.batchNumber}
                        </TableCell>
                        <TableCell>{batch.name}</TableCell>
                        <TableCell className="max-w-[280px] whitespace-normal">
                          {batch.description ?? "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => openEditBatchModal(batch)}
                          >
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Serial numbers</CardTitle>
            <CardAction>
              <Button onClick={openCreateSerialRangeModal}>
                Add serial range
              </Button>
            </CardAction>
          </CardHeader>
          <CardContent>
            {serialRangeLoadingState === LoadingState.Loading ? (
              <div className="flex h-24 items-center justify-center">
                <Spinner className="size-5" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product type</TableHead>
                    <TableHead>Start</TableHead>
                    <TableHead>End</TableHead>
                    <TableHead>Remaining</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Build start</TableHead>
                    <TableHead>Build end</TableHead>
                    <TableHead>Firmware v.</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {serialRangeLoadingState === LoadingState.Error ? (
                    <TableRow>
                      <TableCell
                        colSpan={SERIAL_RANGE_TABLE_COLUMN_COUNT}
                        className="text-center text-sm text-destructive"
                      >
                        Unable to load serial ranges.
                      </TableCell>
                    </TableRow>
                  ) : serialRanges.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={SERIAL_RANGE_TABLE_COLUMN_COUNT}
                        className="text-center text-sm text-muted-foreground"
                      >
                        No serial ranges yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    serialRanges.map((serialRange) => (
                      <TableRow key={serialRange.id}>
                        <TableCell className="font-medium">
                          {`${serialRange.productType?.code ?? "N/A"} - ${serialRange.productType?.name ?? "Unknown"}`}
                        </TableCell>
                        <TableCell>
                          {formatSerialNumber(serialRange.startNumber)}
                        </TableCell>
                        <TableCell>
                          {formatSerialNumber(serialRange.endNumber)}
                        </TableCell>
                        <TableCell>
                          {getRemainingSequentialSerials(serialRange)}
                        </TableCell>
                        <TableCell className="max-w-[280px] whitespace-normal">
                          {serialRange.buildDescription ?? "—"}
                        </TableCell>
                        <TableCell>
                          {format(
                            new Date(serialRange.buildStartDate),
                            "yyyy-MM-dd"
                          )}
                        </TableCell>
                        <TableCell>
                          {serialRange.buildEndDate
                            ? format(
                                new Date(serialRange.buildEndDate),
                                "yyyy-MM-dd"
                              )
                            : "—"}
                        </TableCell>
                        <TableCell>
                          {serialRange.firmwareVersion ?? "—"}
                        </TableCell>
                        <TableCell className="max-w-[280px] whitespace-normal">
                          {serialRange.notes ?? "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              openEditSerialRangeModal(serialRange)
                            }
                          >
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={openModal === "batch"}
        onOpenChange={(open) => {
          if (open) {
            setOpenModal("batch");
          } else {
            setOpenModal(undefined);
          }
        }}
      >
        <DialogContent
          showCloseButton={false}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
        >
          <BatchForm
            key={selectedBatch?.id ?? "create-batch"}
            onClose={() => setOpenModal(undefined)}
            onSubmit={() => updateBatches(true)}
            batch={selectedBatch}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={openModal === "serial"}
        onOpenChange={(open) => {
          if (open) {
            setOpenModal("serial");
          } else {
            setOpenModal(undefined);
          }
        }}
      >
        <DialogContent
          showCloseButton={false}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
        >
          <SerialRangeForm
            onClose={() => setOpenModal(undefined)}
            onSubmit={() => updateSerialRanges(true)}
            productTypes={productTypes}
            serialRange={selectedSerialRange}
          />
        </DialogContent>
      </Dialog>
    </PageTemplate>
  );
};
